import { useCallback, useMemo } from "react";
import {
    useQuery,
    useQueryClient,
    UseQueryResult,
} from "@tanstack/react-query";
import {
    DEFAULT_PLAN_CATALOG_VERSION,
    FLAT_RATE_PLANS,
    PLAN_CATALOG_VERSION,
} from "@common/flatRateBilling";
import {
    ACTIVE_SUBSCRIPTION_STATUSES,
    SubscriptionStatus,
} from "@common/billing";
import {
    BillingStatus,
    SubscriptionDetails,
    SubscriptionType,
} from "@common/types/billing";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBillingStatus } from "@lib/billing";
import { useUserStatus } from "./useUserStatus";
import { useActiveWorkspaceDetails } from "./useActiveWorkspaceDetails";

export const billingQueryKeys = {
    all: () => ["billing"] as const,
    status: (workspaceId: string | null) =>
        [...billingQueryKeys.all(), "status", workspaceId] as const,
    info: () => [...billingQueryKeys.all(), "info"] as const,
    history: () => [...billingQueryKeys.all(), "history"] as const,
} as const;

interface UseBillingStatus {
    query: UseQueryResult<BillingStatus | undefined, unknown>;
    invalidate: () => Promise<void>;
    isFetched: boolean;

    // common
    totalUsage: number;
    isSubscribed: boolean;
    subscriptionDetails: SubscriptionDetails | null;
    product: string;
    periodEnd: Date | null;
    cancelsAt: Date | null;
    subscriptionType: SubscriptionType;

    // flat rate billing
    currentPlan: FLAT_RATE_PLANS;
    nextPlan: FLAT_RATE_PLANS;
    trialAvailable: boolean;
    planCatalogVersion: PLAN_CATALOG_VERSION;
    plans: FlatRatePlan[];
    isTrialing: boolean;
    isDowngrading: boolean;
    bookedEnterpriseCall: boolean;
    isLegacyPlan: boolean;
    /** True when workspace is on the v2 billing catalog. */
    isV2Plan: boolean;
    /** True when workspace is on the v3 (licensed, single-interval) billing catalog. */
    isV3Plan: boolean;
    /** Total transaction count across all workspace entities (for 250-threshold progress). */
    transactionCount: number;
}

export function useBillingStatus(): UseBillingStatus {
    const { workspaceId, activeWorkspaceKey } = useScope();
    const activeWorkspace = useActiveWorkspaceDetails();
    const client = useQueryClient();
    const { data: userStatus } = useUserStatus();
    const bookedEnterpriseCall = userStatus?.user.bookedEnterpriseCall ?? false;
    const query = useQuery({
        queryKey: billingQueryKeys.status(activeWorkspaceKey),
        queryFn: () =>
            workspaceId ? getBillingStatus(workspaceId) : undefined,
        staleTime: Infinity,
        enabled:
            !!workspaceId || !activeWorkspace?.organizationAssignments.length,
    });

    const invalidate = useCallback(
        () => client.invalidateQueries({ queryKey: billingQueryKeys.all() }),
        [client],
    );

    return useMemo(() => {
        if (!query.data) {
            return {
                query,
                invalidate,
                isFetched: false,
                totalUsage: 0,
                plans: [],
                subscriptionDetails: null,
                currentPlan: FLAT_RATE_PLANS.FREE,
                product: "free",
                trialAvailable: false,
                planCatalogVersion: DEFAULT_PLAN_CATALOG_VERSION,
                isSubscribed: false,
                isTrialing: false,
                isDowngrading: false,
                cancelsAt: null,
                periodEnd: null,
                subscriptionType: SubscriptionType.FLAT_RATE,
                nextPlan: FLAT_RATE_PLANS.FREE,
                bookedEnterpriseCall,
                includePreviousYear: false,
                isLegacyPlan: false,
                isV2Plan: false,
                isV3Plan: false,
                transactionCount: 0,
            };
        }

        const subscriptionDetails = query.data.subscriptionDetails;

        const isTrialing =
            query.data.subscriptionDetails?.status ===
            SubscriptionStatus.TRIALING;

        let subscriptionType: SubscriptionType;

        if (
            !subscriptionDetails ||
            subscriptionDetails.status !== SubscriptionStatus.ACTIVE
        ) {
            subscriptionType = SubscriptionType.FLAT_RATE;
        } else {
            subscriptionType = subscriptionDetails.type;
        }

        const nextPlan =
            subscriptionDetails?.nextPeriodPlan ?? FLAT_RATE_PLANS.FREE;

        const billingOwnedByOrganization =
            !!activeWorkspace?.organizationAssignments.some(
                (oa) => oa.billingOwnership,
            );

        const product = billingOwnedByOrganization
            ? "organization"
            : (subscriptionDetails?.product ?? "free");

        const isSubscribed = billingOwnedByOrganization
            ? true
            : Boolean(
                  subscriptionDetails?.status &&
                  ACTIVE_SUBSCRIPTION_STATUSES.includes(
                      subscriptionDetails.status,
                  ),
              );

        const currentPlan = billingOwnedByOrganization
            ? (activeWorkspace?.workspace.plan ?? FLAT_RATE_PLANS.FREE)
            : (subscriptionDetails?.plan ?? FLAT_RATE_PLANS.FREE);

        const trialAvailable =
            billingOwnedByOrganization ||
            activeWorkspace?.workspace.planBeforeTransfer
                ? false
                : query.data.trialAvailable;

        // Exclude org-managed workspaces: the backend rejects the upgrade
        // request for them. Exclude trialing and unsubscribed users: they are
        // not on a paid subscription so migrating Stripe prices does not apply.
        const isLegacyPlan =
            !billingOwnedByOrganization &&
            !isTrialing &&
            isSubscribed &&
            query.data.planCatalogVersion === PLAN_CATALOG_VERSION.V1;

        const isV2Plan =
            query.data.planCatalogVersion === PLAN_CATALOG_VERSION.V2;

        const isV3Plan =
            query.data.planCatalogVersion === PLAN_CATALOG_VERSION.V3;

        return {
            query,
            invalidate,
            isFetched: query.isFetched && query.isSuccess,
            totalUsage: query.data.totalUsage,
            plans: query.data.plans,
            subscriptionDetails,
            product,
            currentPlan,
            nextPlan,
            trialAvailable,
            planCatalogVersion: query.data.planCatalogVersion,
            isSubscribed,
            isTrialing,
            isDowngrading: currentPlan !== nextPlan,
            cancelsAt: subscriptionDetails?.cancelsAt ?? null,
            periodEnd: subscriptionDetails?.periodEndsAt ?? null,
            subscriptionType,
            bookedEnterpriseCall,
            isLegacyPlan,
            isV2Plan,
            isV3Plan,
            transactionCount: query.data.transactionCount,
        };
    }, [activeWorkspace, bookedEnterpriseCall, invalidate, query]);
}
